import { cn } from '@/lib/utils'
import { FormProps } from '@alphabait/ui/form'
import { GripVertical, Trash2, CheckCircle2 } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useController } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { tokenState } from '../../../store/atoms/token'
import { uploadFileGeneric } from '../../../utils/file-upload-helper'
import FileUpload from '../../file-upload'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { AlphaTypes, CustomPictureChoiceInput } from '../custom-types'
import i18n from '../../../i18n'

interface PictureOption {
  value: string
  image: string
  fileId?: string
  description?: string
}

export default function CustomPictureChoiceComponent({
  field,
  formProps,
  previewMode,
}: {
  field: CustomPictureChoiceInput
  formProps: FormProps
  previewMode: boolean
}) {
  const s3Url = import.meta.env.VITE_S3_URL

  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  return (
    <div>
      <div className="flex flex-wrap gap-4">
        {/* Defensive: ensure options or field.options is always an array */}
        {field?.options?.map((option: PictureOption) => (
          <div
            key={option.value}
            className={cn(
              'relative rounded p-1 flex flex-col shadow-md rounded-md border border-border group',
              value === option.value &&
                'border border-black cursor-pointer rounded-md',
            )}
            onClick={() => (!previewMode ? onChange(option.value) : undefined)}
          >
            <div className="w-36 flex flex-col min-h-36">
              <div className="w-full h-28 flex-shrink-0">
                <img
                  src={`${s3Url}/${option.fileId}`}
                  alt={option?.description || 'option'}
                  className="w-full h-full object-fit rounded-md"
                />
              </div>
              <div className="cursor-pointer p-1 mt-auto flex ">
                <div className="flex gap-2 items-start justify-start w-full">
                  {value === option.value && (
                    <CheckCircle2
                      className="h-5 w-5 flex-shrink-0 mr-1 m-auto"
                      fill="black"
                      stroke="white"
                      strokeWidth={2}
                    />
                  )}
                  <div className="relative w-full">
                    <div
                      className="flex text-sm line-clamp-2 w-full leading-tight break-words hyphens-auto text-left"
                      title={option?.description || ''}
                    >
                      {option?.description}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DraggablePictureOption({
  option,
  s3Url,
  handleDescriptionChange,
  handleDeleteOption,
}: {
  option: PictureOption
  s3Url: string
  handleDescriptionChange: (value: string, desc: string) => void
  handleDeleteOption: (value: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <div
        className="cursor-grab touch-none pl-2"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-10 w-4 text-gray-400" />
      </div>
      <div className="flex items-center gap-2 flex-1">
        <img
          src={`${s3Url}/${option.fileId}`}
          alt={option.description || 'option'}
          className="w-12 max-h-12 object-cover rounded border border-border"
        />
        <input
          type="text"
          placeholder="Add label"
          value={option.description || ''}
          onChange={(e) =>
            handleDescriptionChange(option.value, e.target.value)
          }
          className="w-full border border-border rounded-md px-2 py-1 text-sm"
        />
      </div>
      <button
        type="button"
        onClick={() => handleDeleteOption(option.value)}
        className="px-1 py-3 hover:bg-gray-100"
      >
        <Trash2 className="h-5 w-5 text-gray-400" />
      </button>
    </div>
  )
}

function PictureChoiceOptionsComponent({
  field,
  formProps,
}: {
  field: CustomPictureChoiceInput
  formProps: FormProps
}) {
  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  const [options, setOptions] = useState<
    {
      value: string
      image: string
      fileId: string
    }[]
  >([])
  // Create a ref for the FileUpload component to access its methods
  const fileUploadRef = useRef<{ handleReset: () => void }>(null)

  useEffect(() => {
    value && setOptions(value)
  }, [value])

  const { id } = useParams()
  const token = useRecoilValue(tokenState)
  const s3Url = import.meta.env.VITE_S3_URL

  const generateUniqueId = () => {
    return `option-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  const handleFileUpload = async (file: File | undefined) => {
    if (!file) {
      // This is a reset event from the FileUpload component
      return
    }

    const optionId = generateUniqueId()

    try {
      const response = await uploadFileGeneric({
        file,
        uploadUrl: `/upload/picture-choice`,
        uploadBody: { campaignId: id },
        token: token?.__raw,
      })

      // Parse the response which contains { fileId, thumbnailFileId }
      const { fileId, thumbnailFileId } = JSON.parse(response)

      const newOption = {
        value: optionId,
        image: `${s3Url}/${thumbnailFileId}`,
        fileId: fileId,
      }

      const updatedOptions = [...options, newOption]
      setOptions(updatedOptions)
      onChange(updatedOptions)

      // Reset the file upload component after successful upload
      fileUploadRef.current?.handleReset()
    } catch (error) {
      console.error('Error uploading file:', error)
    }
  }

  const handleDescriptionChange = (
    optionValue: string,
    description: string,
  ) => {
    const updatedOptions = options.map((option) =>
      option.value === optionValue ? { ...option, description } : option,
    )
    setOptions(updatedOptions)
    onChange(updatedOptions)
  }

  const handleDeleteOption = (optionValue: string) => {
    const updatedOptions = options.filter(
      (option) => option.value !== optionValue,
    )
    setOptions(updatedOptions)
    onChange(updatedOptions)
  }

  const moveOption = (dragIndex: number, hoverIndex: number) => {
    const newOptions = arrayMove(options, dragIndex, hoverIndex)
    setOptions(newOptions)
    onChange(newOptions)
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  return (
    <div>
      <div className="mb-1 font-medium">
        {i18n.t('questionnaire_builder.upload_pictures')}
      </div>
      <p className="mb-2 text-sm text-gray-500">
        {i18n.t('questionnaire_builder.picture_upload_hint')}
      </p>
      <FileUpload
        ref={fileUploadRef}
        allowedFileTypes={['jpg', 'png']}
        onFileUpload={handleFileUpload}
        testId="picture-choice-upload"
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event: DragEndEvent) => {
          const { active, over } = event
          if (active.id !== over?.id) {
            const oldIndex = options.findIndex(
              (option) => option.value === active.id,
            )
            const newIndex = options.findIndex(
              (option) => option.value === over?.id,
            )
            moveOption(oldIndex, newIndex)
          }
        }}
      >
        <SortableContext
          items={options.map((option) => option.value)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-4 mt-4">
            {options.map((option) => (
              <DraggablePictureOption
                key={option.value}
                option={option}
                s3Url={s3Url}
                handleDescriptionChange={handleDescriptionChange}
                handleDeleteOption={handleDeleteOption}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const getCustomPictureChoiceProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'options',
      type: 'render',
      validations: ['required'],
      render: (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        field: any,
        formProps: FormProps,
      ) => {
        return (
          <PictureChoiceOptionsComponent
            field={field as CustomPictureChoiceInput}
            formProps={formProps}
          />
        )
      },
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]

// For backwards compatibility
// eslint-disable-next-line react-refresh/only-export-components
export const customPictureChoiceProperties = getCustomPictureChoiceProperties()
